import {RefObject, useLayoutEffect} from 'react';

import {head, isNil, isNotNil, last} from 'ramda';

import {FOCUSABLE_ELEMENTS_SELECTOR, useLatest} from 'shared/web';

// Open dialogs in the order they were opened. Only the last one, the topmost,
// handles the keyboard, so a nested dialog keeps focus and Escape to itself.
const openDialogTokens: object[] = [];

// Without checkVisibility there is no layout to ask (jsdom in consumer tests), so every
// element counts as rendered; measuring it there would hide them all and swallow every Tab
const isRendered = (element: HTMLElement) =>
  !('checkVisibility' in element) ||
  element.checkVisibility({checkVisibilityCSS: true, visibilityProperty: true});

const isRadio = (element: HTMLElement): element is HTMLInputElement =>
  element instanceof HTMLInputElement && element.type === 'radio';

const follows = (reference: Node, other: Node) =>
  (reference.compareDocumentPosition(other) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;

// Elements Tab stops at. Buttons with tabindex="-1" (e.g. the Choice indicator or
// NumberInput steppers) match the focusable selector but are skipped by Tab, and so
// are the unchecked radios of a group that has a checked one.
function findTabbableElements(container: HTMLElement | null) {
  if (isNil(container)) {
    return [];
  }

  const candidates = Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS_SELECTOR)
  ).filter((element) => element.tabIndex >= 0 && isRendered(element));
  const checkedRadioNames = new Set(
    candidates.filter(isRadio).flatMap((radio) => (radio.checked ? [radio.name] : []))
  );

  return candidates.filter(
    (element) =>
      !isRadio(element) ||
      element.checked ||
      element.name === '' ||
      !checkedRadioNames.has(element.name)
  );
}

const canTakeFocus = (element: HTMLElement) =>
  element.isConnected && !element.matches(':disabled') && isRendered(element);

// A node outside the dialog that sits above it: a popup portaled after the dialog
// in the document. Layers that were open before it, such as the AI assistant, are
// covered by the overlay, so they count as the page behind.
const isInLayerAbove = (container: HTMLElement | null, node: EventTarget | null) =>
  isNotNil(container) &&
  node instanceof Node &&
  !container.contains(node) &&
  follows(container, node);

export interface UseDialogKeyboardTrapProps {
  isOpen: boolean;
  contentRef: RefObject<HTMLElement | null>;
  onEscape: () => void;
}

/**
 * Keeps keyboard focus inside the topmost open dialog: Tab and Shift+Tab wrap
 * around its focusable elements, and focus that the keyboard or code moves to
 * the page behind the dialog is returned to it. Escape closes only the topmost
 * dialog, which is the most recently opened one.
 *
 * Layers above the dialog are left alone, so focus, Tab and Escape inside them
 * belong to them. A layer counts as above when it is portaled after the dialog
 * in the document, as popups opened from the dialog (dropdowns, date pickers)
 * are. Anything that was open before the dialog, the AI assistant included, is
 * covered by its overlay and counts as the page behind.
 */
export function useDialogKeyboardTrap(props: UseDialogKeyboardTrapProps) {
  const onEscapeRef = useLatest(props.onEscape);
  const contentRef = props.contentRef;

  // A layout effect, so the trap is gone before the dialog returns focus to its trigger in
  // its own layout effect; the closing content stays in the document while it animates out
  useLayoutEffect(
    function trapKeyboardWhileOpen() {
      if (!props.isOpen) {
        return;
      }

      const token = {};
      openDialogTokens.push(token);

      // A focus change right after a pointer press is the user clicking something
      // rendered above the overlay (e.g. a toast), so it is not pulled back. Only
      // focus landing on the pressed element or around it counts, a click inside
      // the dialog gives no leave to focus the page behind.
      let pointerDownTarget: Node | null = null;
      let lastFocusedInside: HTMLElement | null = null;
      // Decided before the handlers of the page run, since they may close and
      // unmount the layer (e.g. a dropdown on Escape) before the window sees the event
      let keyDownStartedInLayerAbove = false;

      const isTopmost = () => last(openDialogTokens) === token;

      const isInside = (node: EventTarget | null) =>
        node instanceof Node && contentRef.current?.contains(node) === true;

      const isPointerFocus = (target: Node) =>
        isNotNil(pointerDownTarget) &&
        (pointerDownTarget.contains(target) || target.contains(pointerDownTarget));

      const wrapTab = (event: KeyboardEvent) => {
        const tabbable = findTabbableElements(contentRef.current);
        const first = head(tabbable);
        const lastFocusable = last(tabbable);
        if (isNil(first) || isNil(lastFocusable)) {
          event.preventDefault();
          return;
        }

        const activeElement = document.activeElement;
        // At the edge when Tab has nothing left to reach in its direction, which also
        // covers focus on an element Tab skips (e.g. a clicked NumberInput stepper)
        const isAtEdge =
          activeElement instanceof Node &&
          !tabbable.some((element) =>
            event.shiftKey ? follows(element, activeElement) : follows(activeElement, element)
          );
        const wrapTarget = event.shiftKey ? lastFocusable : first;
        if (isAtEdge || !isInside(activeElement)) {
          event.preventDefault();
          wrapTarget.focus();
        }
      };

      const handlePointerDown = (event: PointerEvent) => {
        pointerDownTarget = event.target instanceof Node ? event.target : null;
      };

      const handleKeyDownCapture = (event: KeyboardEvent) => {
        pointerDownTarget = null;
        keyDownStartedInLayerAbove = isInLayerAbove(contentRef.current, event.target);
      };

      const handleKeyDown = (event: KeyboardEvent) => {
        const isInLayer = keyDownStartedInLayerAbove;
        keyDownStartedInLayerAbove = false;

        if (!isTopmost() || isInLayer) {
          return;
        }

        if (event.key === 'Tab') {
          // Content that handles Tab itself (e.g. a date picker) prevents the default
          if (!event.defaultPrevented) {
            wrapTab(event);
          }
          return;
        }

        if (event.key === 'Escape') {
          event.stopPropagation();
          onEscapeRef.current();
        }
      };

      const handleFocusIn = (event: FocusEvent) => {
        if (!isTopmost() || !(event.target instanceof HTMLElement)) {
          return;
        }

        if (isInside(event.target)) {
          lastFocusedInside = event.target;
          return;
        }

        if (isPointerFocus(event.target) || isInLayerAbove(contentRef.current, event.target)) {
          return;
        }

        // The last focused element may have been disabled or hidden since (e.g. a
        // submitting Save button), focusing it would leave focus behind the dialog
        const fallback =
          isNotNil(lastFocusedInside) && canTakeFocus(lastFocusedInside)
            ? lastFocusedInside
            : head(findTabbableElements(contentRef.current));
        fallback?.focus();
      };

      document.addEventListener('pointerdown', handlePointerDown, true);
      document.addEventListener('keydown', handleKeyDownCapture, true);
      document.addEventListener('focusin', handleFocusIn);
      window.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('pointerdown', handlePointerDown, true);
        document.removeEventListener('keydown', handleKeyDownCapture, true);
        document.removeEventListener('focusin', handleFocusIn);
        window.removeEventListener('keydown', handleKeyDown);

        const index = openDialogTokens.indexOf(token);
        if (index !== -1) {
          openDialogTokens.splice(index, 1);
        }
      };
    },
    [props.isOpen, contentRef, onEscapeRef]
  );
}
